import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, CheckCircle, XCircle, Loader } from 'lucide-react';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyEmail } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid or missing verification token');
      setLoading(false);
      return;
    }

    handleVerification(token);
  }, [searchParams]);

  const handleVerification = async (token) => {
    try {
      const response = await verifyEmail(token);
      setStatus('success');
      setMessage(response.message || 'Email verified successfully!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Email verification failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            {/* Icon */}
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
              status === 'verifying' ? 'bg-blue-100' :
              status === 'success' ? 'bg-green-100' :
              'bg-red-100'
            }`}>
              {status === 'verifying' && <Loader className="text-blue-600 animate-spin" size={40} />}
              {status === 'success' && <CheckCircle className="text-green-600" size={40} />}
              {status === 'error' && <XCircle className="text-red-600" size={40} />}
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {status === 'verifying' && 'Verifying Your Email'}
              {status === 'success' && 'Email Verified!'}
              {status === 'error' && 'Verification Failed'}
            </h1>

            {/* Message */}
            <p className={`text-base mb-8 ${
              status === 'verifying' ? 'text-gray-600' :
              status === 'success' ? 'text-green-700' :
              'text-red-700'
            }`}>
              {status === 'verifying' && 'Please wait while we verify your email address...'}
              {message}
            </p>

            {/* Actions */}
            {status === 'success' && (
              <div className="space-y-3">
                <div className="text-sm text-gray-500">
                  Redirecting to login page in 3 seconds...
                </div>
                <Link
                  to="/login"
                  className="inline-block w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium
                    hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Go to Login
                </Link>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-3">
                <Link
                  to="/login"
                  className="inline-block w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium
                    hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Go to Login
                </Link>
                <p className="text-sm text-gray-600">
                  Need help?{' '}
                  <a href="mailto:support@primeaif.com" className="text-blue-600 hover:text-blue-700">
                    Contact Support
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
